
import React, { useState, useEffect } from 'react';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Bell, Settings, User, CreditCard, LogIn } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/lib/supabase';

const Navbar: React.FC = () => {
  const {
    user,
    userProfile,
    signOut
  } = useAuth();
  const navigate = useNavigate();
  const [stripeAccounts, setStripeAccounts] = useState<Array<{id: string, name: string}>>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  
  useEffect(() => {
    if (user) {
      fetchStripeAccounts();
    }
  }, [user]);
  
  const fetchStripeAccounts = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('list-stripe-accounts', {
        body: { userId: user?.id }
      });
      
      if (error) {
        console.error('Error fetching Stripe accounts:', error);
        return;
      }
      
      if (data?.accounts && data.accounts.length > 0) {
        setStripeAccounts(data.accounts);
        // Set the first account as default if none is selected
        if (!selectedAccount && data.accounts.length > 0) {
          setSelectedAccount(data.accounts[0].id);
          localStorage.setItem('selectedStripeAccount', data.accounts[0].id);
        }
      }
    } catch (error) {
      console.error('Exception fetching Stripe accounts:', error);
    }
  };
  
  const handleAccountChange = (accountId: string) => {
    setSelectedAccount(accountId);
    localStorage.setItem('selectedStripeAccount', accountId);
    
    // Notify the user about the account change
    const account = stripeAccounts.find(acc => acc.id === accountId);
    if (account) {
      toast("Compte Stripe changé", {
        description: `Vous utilisez maintenant le compte ${account.name}`
      });
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error: any) {
      console.error("Erreur lors de la déconnexion:", error);
      toast.error("Erreur de déconnexion", {
        description: error.message || "Une erreur est survenue lors de la déconnexion."
      });
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };
  
  return (
    <header className="border-b bg-white dark:bg-gray-950 px-4 py-3 flex items-center justify-between">
      <div className="flex items-center">
        {user && <SidebarTrigger className="mr-4" />}
        <div className="hidden md:block font-semibold text-lg">ZanEstate : SaaS financement immobilier</div>
      </div>
      
      <div className="flex items-center gap-3">
        {user ? (
          <>
            {stripeAccounts.length > 1 && (
              <div className="hidden md:block">
                <Select value={selectedAccount} onValueChange={handleAccountChange}>
                  <SelectTrigger className="w-[180px]">
                    <CreditCard className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Sélectionner un compte" />
                  </SelectTrigger>
                  <SelectContent>
                    {stripeAccounts.map(account => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
        
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <DropdownMenuLabel>
                  <div className="font-medium">{userProfile?.prenom} {userProfile?.nom || 'Utilisateur'}</div>
                  <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {stripeAccounts.length > 1 && (
                  <>
                    <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                      Comptes Stripe
                    </DropdownMenuLabel>
                    {stripeAccounts.map(account => (
                      <DropdownMenuItem 
                        key={account.id} 
                        className={`cursor-pointer ${selectedAccount === account.id ? 'bg-accent' : ''}`}
                        onClick={() => handleAccountChange(account.id)}
                      >
                        <CreditCard className="mr-2 h-4 w-4" /> 
                        {account.name}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="cursor-pointer flex w-full">
                    <User className="mr-2 h-4 w-4" /> Profil
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer flex w-full">
                    <Settings className="mr-2 h-4 w-4" /> Paramètres
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="text-red-500 cursor-pointer" 
                  onClick={handleLogout}
                >
                  Se déconnecter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          <Button 
            variant="default" 
            size="sm" 
            className="gap-2"
            onClick={handleLogin}
          >
            <LogIn className="h-4 w-4" />
            Se connecter
          </Button>
        )}
      </div>
    </header>
  );
};

export default Navbar;
